import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { ContentModalService, MsiContentModalRef, Toast, ToastService } from '@msi/cobalt';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { PrivacyModel } from 'src/app/interfaces/privacy-model.interface';
import { AgreementsModalInvokerService } from 'src/app/services/agreements-modal-invoker/agreements-modal-invoker.service';
import { modalConfig } from 'src/app/services/basic-modal/basic-modal-configs.const';
import { BasicModalService } from 'src/app/services/basic-modal/basic-modal.service';
import { LoadingErrorsService } from 'src/app/services/loading-errors/loading-errors.service';
import { SelfRegistrationService } from 'src/app/services/self-registration/self-registration.service';
import { onlyFalsy } from 'src/app/utils/rxjs-custom-operators/rxjs-custom-operators.utils';
import { SpinnerModule } from '@msi/cobalt/spinner';
import { CheckboxModule } from '@msi/cobalt/checkbox';
import { NgTemplateOutlet } from '@angular/common';

@Component({
	selector: 'app-agreements-acceptance',
	templateUrl: './agreements-acceptance.component.html',
	styleUrls: ['./agreements-acceptance.component.scss'],
	standalone: true,
	imports: [CheckboxModule, SpinnerModule, NgTemplateOutlet],
})
export class AgreementsAcceptanceComponent implements OnInit, OnDestroy {
	@Output() provideOpenFunc = new EventEmitter();
	@Output() changeCurrentState = new EventEmitter<string>();
	@Input() inMfe: boolean;
	@ViewChild('tpl') private templateRef: TemplateRef<any>;

	agreementsToBeAccepted: PrivacyModel[];

	public modalRef: MsiContentModalRef;

	currentState: string;
	reviewUpdatedAgreementsToast: Toast;
	allSelected: boolean;
	someChecked: boolean;

	private destroy: Subject<void> = new Subject();

	constructor(
		private agreementsModalInvokerService: AgreementsModalInvokerService,
		private toastService: ToastService,
		private contentModalService: ContentModalService,
		private basicModalService: BasicModalService,
		private loadingErrorsService: LoadingErrorsService,
		private selfRegistrationService: SelfRegistrationService
	) {}

	ngOnInit(): void {
		this.currentState = '';
		this.allSelected = false;
		this.someChecked = false;

		/** Load component specific data only if agreements data has been loaded */
		if (!this.inMfe) {
			this.provideOpenFunc.emit(() => {
				this.agreementsModalInvokerService.loadingData$
					.pipe(onlyFalsy(), takeUntil(this.destroy), take(1))
					.subscribe(() => {
						/** Open function is provided by the modal which comes from the AgreementsInvokerComponent
						 * hence it is not in the MFE and override the value coming from Self Registration Component */
						this.agreementsToBeAccepted = this.agreementsModalInvokerService.agreementsToBeAccepted;
						this.openAgreementsModal();
					});
			});
		} else {
			/** Load component specific data only if agreements data has been loaded */
			this.selfRegistrationService.status$.pipe(takeUntil(this.destroy), take(1)).subscribe(() => {
				this.agreementsToBeAccepted = this.selfRegistrationService.agreements;
				this.currentState = 'agreements';
			});
		}
	}

	ngOnDestroy() {
		this.toastService.hideToast(this.reviewUpdatedAgreementsToast);
		this.destroy.next();
		this.destroy.complete();
		// Note: If running in an iframe, the whole iframe ceasing to exist is sufficient to close event listeners
	}

	/**
	 * Checks all other privacy tabs in privacies based on the parameter
	 */
	selectAll(checkedEvent: any) {
		// Make sure it actually changed
		const checked = checkedEvent?.srcElement?.checked ?? true;
		if (this.allSelected === checked) return;
		this.allSelected = checked;
		this.agreementsToBeAccepted.forEach(p => (p.checked = checked));
		this.someSelected();
	}

	/**
	 * Selects a particular agreement from within agreementsToBeAccepted[] based on the bool passed (to set the 'checked' property in PrivacyModel)
	 */
	selectPrivacyBox(id: string, checkedEvent: any) {
		const checked = checkedEvent?.srcElement?.checked ?? true;
		const agreementToBeChecked = this.agreementsToBeAccepted.find(p => p.agreementId === id);
		// Make sure it actually changed
		if (agreementToBeChecked === null || agreementToBeChecked === undefined || agreementToBeChecked.checked === checked)
			return;
		agreementToBeChecked.checked = checked;
		// From the array, even if one item was not unchecked, then, it means all checkboxes in agreementsToBeAccepted[] are checked
		this.allSelected = !this.agreementsToBeAccepted.some(p => !p.checked);
		this.someSelected();
	}

	/** Sets someChecked based on whether there is at least one agreement item selected from agreementsToBeAccepted[] (for the indeterminate check) */
	someSelected() {
		this.someChecked = this.agreementsToBeAccepted?.some(p => p.checked);
	}

	/**
	 * When clicked on accept button to register the agency, redirect to Orchestrate
	 */
	acceptButtonClick() {
		// if the user is present, send the agreements acceptance confirmation to the orch backend
		if (!this.inMfe) {
			this.agreementsModalInvokerService.patchUserAgreementsOrc().subscribe(() => {
				this.toastService.hideToast(this.reviewUpdatedAgreementsToast);
				// Called because afterClose() handles closing this before because we might not want to wait for the api service delay to affect the processing.
				this.modalRef?.close();
			});
		} else {
			this.currentState = 'agreements-accepted';
			this.selfRegistrationService.acceptButtonClicked().subscribe({
				complete: () => {
					this.changeCurrentState.emit('button');
				},
			});
		}
	}

	/** Open the end user agreements modal if data is loaded and user has not accepted it */
	openAgreementsModal() {
		// Close if the current modal is already open (to avoid stacking the same modal)
		this.modalRef?.close();
		this.currentState = 'agreements';
		this.showReviewToastIfAgreementsUpdate();
		this.modalRef = this.contentModalService.open(this.templateRef, { disableClose: true, hasBackdrop: true });
	}

	confirmToCloseAgreementModal() {
		this.modalRef?.close();
		this.toastService.hideToast(this.reviewUpdatedAgreementsToast);
		this.basicModalService.createModalUnfiltered(modalConfig.reviewAgreementsClose).subscribe(response => {
			if (!this.inMfe) {
				if (!response || response.canceled) {
					this.openAgreementsModal();
				} else {
					this.loadingErrorsService.openErrorPage(
						'403',
						$localize`Agreements not accepted`,
						$localize`Orchestrate cannot be accessed until the end user agreements are accepted.`
					);
				}
			} else {
				if (!response?.canceled) {
					this.agreementsToBeAccepted.forEach(p => (p.checked = false));
					this.someSelected();
					this.changeCurrentState.emit('button');
				} else {
					this.changeCurrentState.emit('self-registration-pressed');
				}
			}
		});
	}

	redirectToOrchestrate() {
		this.selfRegistrationService.redirectToOrchestrate();
	}

	private showReviewToastIfAgreementsUpdate() {
		if (this.agreementsToBeAccepted?.length > 0 && this.agreementsModalInvokerService.agreementsUpdatedSinceLastVisit) {
			// If any agreement has been updated, we want to display
			// a text saying that there is change in at least one agreement

			this.reviewUpdatedAgreementsToast = this.toastService.warning(
				this.agreementsToBeAccepted.length > 1
					? $localize`Please review the agreements which have been updated since you last accepted them.`
					: $localize`Please review the agreement which has been updated since you last accepted it.`,
				null,
				{
					autoDismiss: 10_000,
				}
			);
		}
	}
}

// Copyright (c) Motorola Solutions Inc. All Rights Reserved.
